"use client"

import * as React from "react"
import { Check, Minus } from "lucide-react"
import { cn } from "@/lib/utils"

export interface CheckboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'checked'> {
  onCheckedChange?: (checked: boolean) => void
  checked?: boolean | "indeterminate"
}

const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  ({ className, onCheckedChange, checked, ...props }, ref) => {
    const inputRef = React.useRef<HTMLInputElement>(null)

    React.useImperativeHandle(ref, () => inputRef.current!)

    React.useEffect(() => {
      if (inputRef.current) {
        inputRef.current.indeterminate = checked === "indeterminate"
      }
    }, [checked])

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      onCheckedChange?.(event.target.checked)
      props.onChange?.(event)
    }

    const isChecked = checked === true

    return (
      <div className="relative inline-flex">
        <input
          type="checkbox"
          ref={inputRef}
          className={cn(
            "peer h-4 w-4 shrink-0 rounded-sm border border-muted-foreground/40 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
            "appearance-none",
            className,
            isChecked && "bg-foreground border-foreground text-background",
            checked === "indeterminate" && "bg-foreground border-foreground text-background",
          )}
          checked={isChecked}
          onChange={handleChange}
          {...props}
        />
        {isChecked && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <Check className="h-3 w-3 text-background" />
          </div>
        )}
        {checked === "indeterminate" && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <Minus className="h-3 w-3 text-background" />
          </div>
        )}
      </div>
    )
  }
)

Checkbox.displayName = "Checkbox"

export { Checkbox }
